import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GLOSSARY } from "../data/glossary";
import { BookMarked, Search, ChevronDown, Info, Printer } from "lucide-react";

export default function Glossaire() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("Tous");
  const [open, setOpen] = useState({});

  const cats = useMemo(() => ["Tous", ...Array.from(new Set(GLOSSARY.map((g) => g.cat)))], []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return GLOSSARY.filter((g) => {
      const matchCat = cat === "Tous" || g.cat === cat;
      const matchQ = !q || g.term.toLowerCase().includes(q) || g.short.toLowerCase().includes(q);
      return matchCat && matchQ;
    }).sort((a, b) => a.term.localeCompare(b.term, "fr"));
  }, [query, cat]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-primary">
            <BookMarked size={14} /> Références
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Glossaire</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-xl">
            Les termes techniques expliqués simplement. Touchez « En savoir plus » pour une explication détaillée.
          </p>
        </div>
        <button onClick={() => window.print()} data-testid="glossary-print" className="print:hidden inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors shrink-0">
          <Printer size={16} /> Imprimer
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="glossary-search"
            placeholder="Rechercher un terme…"
            className="w-full rounded-sm border border-border bg-card pl-9 pr-3 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`glossary-cat-${c}`}
            className={`rounded-sm border px-3 py-1.5 text-xs font-medium transition-colors ${
              cat === c ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map((g, i) => {
          const isOpen = open[g.term];
          return (
            <motion.div
              key={g.term}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.02, 0.3) }}
              className="rounded-sm border border-border bg-card p-4"
              data-testid={`glossary-item-${g.term}`}
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-bold text-lg leading-tight">{g.term}</h3>
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded-sm px-1.5 py-0.5 shrink-0">
                  {g.cat}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">{g.short}</p>
              <button
                onClick={() => setOpen((o) => ({ ...o, [g.term]: !o[g.term] }))}
                data-testid={`glossary-more-${g.term}`}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all"
              >
                <Info size={15} /> En savoir plus
                <ChevronDown size={15} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <p className="text-sm leading-relaxed mt-3 pt-3 border-t border-border">{g.more}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <div className="text-center text-sm text-muted-foreground py-12">Aucun terme trouvé.</div>
      )}
    </div>
  );
}
